import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { FormAttachmentUploadRequirements } from '../attachments/form-attachment-requirements';
import { GeneralFormData } from '../general-form-data.model';
import { FormStep } from '../form-steps.interface';
import { parseLocalDate } from '../../../utils/date-time-formatter';



export class ConfinedSpaceAlternateEntryForm {

    id?: string;
    submittedByName: string = '';
    submittedByEmail: string = '';
    submissionDate?: Date;



    // Confined space alternate entry form specific properties
    spaceLocation: string = '';
    entryDate: string = '';
    entryDuration: string = '';
    entrant1: string = '';
    entrant2: string = '';

    // Physical Hazards
    physicalHazard1: string = '';
    physicalHazardAction1: string = '';
    physicalHazardActionDescription1: string = '';
    physicalHazard2: string = '';
    physicalHazardAction2: string = '';
    physicalHazardActionDescription2: string = '';
    physicalHazard3: string = '';
    physicalHazardAction3: string = '';
    physicalHazardActionDescription3: string = '';
    physicalHazard4: string = '';
    physicalHazardAction4: string = '';
    physicalHazardActionDescription4: string = '';
    physicalHazard5: string = '';
    physicalHazardAction5: string = '';
    physicalHazardActionDescription5: string = '';

    // Atmospheric Hazards
    atmosphericHazard1: string = '';
    atmosphericHazardAction1: string = '';
    atmosphericHazardActionDescription1: string = '';
    atmosphericHazard2: string = '';
    atmosphericHazardAction2: string = '';
    atmosphericHazardActionDescription2: string = '';

    //General Hazard action and Description, only stored on legacy WP forms
    generalHazardActions:string = '';
    generalHazardActionDescriptions: string = '';

    forcedAirVentilationRequired: string = '';
    ventilationType: string = '';
    multipleUnitsRequired: string = '';
    // Oxygen monitoring results (3 readings)
    oxygenResult1: string = '';
    oxygenResult2: string = '';
    oxygenResult3: string = '';
    // Hydrogen Sulfide monitoring results (3 readings)
    hydrogenSulfideResult1: string = '';
    hydrogenSulfideResult2: string = '';
    hydrogenSulfideResult3: string = '';
    // Carbon Monoxide monitoring results (3 readings)
    carbonMonoxideResult1: string = '';
    carbonMonoxideResult2: string = '';
    carbonMonoxideResult3: string = '';
    // Lower Explosive Limit monitoring results (3 readings)
    lowerExplosiveLimitResult1: string = '';
    lowerExplosiveLimitResult2: string = '';
    lowerExplosiveLimitResult3: string = '';
    
    gasDetectorModel: string = '';
    calibrationDaysLeft: string = '';
    additionalNotes: string = '';
    responsiblePersonName: string = '';
    responsiblePersonJobTitle: string = '';
    signature: Array<string> = [];
  

    constructor(data?: Partial<ConfinedSpaceAlternateEntryForm>) {
        // Set default date to today if not provided
        if (data) {
            Object.assign(this, {
                ...data,
                submissionDate: data.submissionDate ? new Date(data.submissionDate) : null,
                entryDate: data.entryDate ? parseLocalDate(data.entryDate as any) : null,
            });
        }
    }


    static getFormSteps(): FormStep[] {
        return [
            {
                id: 'basic',
                shortTitle: 'Basic Info',
                title: 'Basic Info',
                icon: 'person-outline',
                description: 'Enter the confined space alternate entry form details',
                sections: [
                    {
                        fields: ['spaceLocation', 'entryDate', 'entryDuration']
                    },
                    {
                        title: 'List of Entrants',
                        description: 'Personnel entering the confined space',
                        fields: ['entrant1', 'entrant2']
                    }
                ]
            },
            {
                id: 'hazards',
                shortTitle: 'Hazards',
                title: 'Hazards',
                icon: 'warning-outline',
                description: 'Identify all physical and atmospheric hazards present in the space, and list each action taken to eliminate the hazard',
                sections: [
                    {
                        title:"Physical Hazards In The Space",
                        subtitle:'Physical Hazard 1',
                        fields: ['physicalHazard1', 'physicalHazardAction1', 'physicalHazardActionDescription1']
                    },
                    {
            
                        subtitle:'Physical Hazard 2',
                        fields: ['physicalHazard2', 'physicalHazardAction2', 'physicalHazardActionDescription2']
                    },
                    {
                      
                        subtitle:'Physical Hazard 3',
                        fields: ['physicalHazard3', 'physicalHazardAction3', 'physicalHazardActionDescription3']
                    },
                    {
                   
                        subtitle:'Physical Hazard 4',
                        fields: ['physicalHazard4', 'physicalHazardAction4', 'physicalHazardActionDescription4']
                    },
                    {
                      
                        subtitle:'Physical Hazard 5',
                        fields: ['physicalHazard5', 'physicalHazardAction5', 'physicalHazardActionDescription5']
                    },
                    {
                        title:"Atmospheric Hazards in the space",
                        subtitle:'Atmospheric Hazard 1',
                        fields: ['atmosphericHazard1', 'atmosphericHazardAction1', 'atmosphericHazardActionDescription1']
                    },
                    {
                      
                        subtitle:'Atmospheric Hazard 2',
                        fields: ['atmosphericHazard2', 'atmosphericHazardAction2', 'atmosphericHazardActionDescription2']
                    },
                    {
                        subtitle:'General Hazard Actions and Descriptions',
                        fields: ['generalHazardActions', 'generalHazardActionDescriptions']
                    }
               
                ]
            },
            {
                id: 'ventilation',
                shortTitle: 'Ventilation & Monitoring',
                title: 'Ventilation & Monitoring',
                icon: 'stats-chart-outline',
                description: 'Ventilation and air monitoring details',
                sections: [
                    {
                        title: 'Forced Air Ventilation',
                        fields: ['forcedAirVentilationRequired', 'ventilationType', 'multipleUnitsRequired']
                    },
                    {
                        title: 'Air Monitoring - Oxygen (O2)',
                        description: 'Permissible Levels 19.5% - 23.5%',
                        fields: ['oxygenResult1', 'oxygenResult2', 'oxygenResult3']
                    },
                    {
                        title: 'Air Monitoring - Hydrogen Sulfide (H2S)',
                        description: 'Permissible Levels 10ppm',
                        fields: ['hydrogenSulfideResult1', 'hydrogenSulfideResult2', 'hydrogenSulfideResult3']
                    },
                    {
                        title: 'Air Monitoring - Carbon Monoxide (CO)',
                        description: 'Permissible Levels 50ppm',
                        fields: ['carbonMonoxideResult1', 'carbonMonoxideResult2', 'carbonMonoxideResult3']
                    },
                    {
                        title: 'Air Monitoring - Lower Explosive Limit (LEL)',
                        description: 'Permissible Levels 10%',
                        fields: ['lowerExplosiveLimitResult1', 'lowerExplosiveLimitResult2', 'lowerExplosiveLimitResult3']
                    },
                    {
                        title: 'Gas Field Monitor',
                        fields: ['gasDetectorModel', 'calibrationDaysLeft']
                    }
                ]
            },
            {
                id: 'additional',
                shortTitle: 'Additional Info',
                title: 'Additional Info',
                icon: 'document-text-outline',
                description: 'Additional information and responsible person details',
                sections: [
                    {
                        fields: ['additionalNotes']
                    },
                    {
                        title: 'Person Responsible for Ensuring the Space is Safe to Enter',
                        fields: ['responsiblePersonName', 'responsiblePersonJobTitle', 'signature']
                    }
                ]
            }
        ]
    }

    static getFormInfo(): GeneralFormData {
        return {
            formType: 'confined-space-alt-entry',
            formTitle: 'Confined Space Alternate Entry Form',
            formIcon: 'enter-outline',
            cardImage: 'assets/form-card-tiles/confined-space-alt-entry.png',
            formAttachmentRequirements: {
                signatures: [
                    {
                        identifier: 'signature',
                        label: this.getFieldMappings()['signature'],
                        required:true
                    }
                ]    
            }
        };
    }

    static getFieldMappings(): Record<string, string> {
        return {

            signature: "Signature",
            submittedByName: "Submitter Name",
            submittedByEmail: "Submitter Email",
            submissionDate: "Submission Date",
            spaceLocation: "Location of the Space",
  entryDate: "Entry Date",
  entryDuration: "Entry Duration",
  entrant1: "Entrants #1",
  entrant2: "Entrants #2",
  physicalHazard1: "Physical Hazard #1",
  physicalHazardAction1: "Action Taken",
  physicalHazardActionDescription1: "Action Description",
  physicalHazard2: "Physical Hazard #2",
  physicalHazardAction2: "Action Taken",
  physicalHazardActionDescription2: "Action Description",
  physicalHazard3: "Physical Hazard #3",
  physicalHazardAction3: "Action Taken",
  physicalHazardActionDescription3: "Action Description",
  physicalHazard4: "Physical Hazard #4",
  physicalHazard5: "Physical Hazard #5",
  physicalHazardAction4: "Action Taken",
  physicalHazardActionDescription4: "Action Description",
  physicalHazardAction5: "Action Taken",
  physicalHazardActionDescription5: "Action Description",
  atmosphericHazard1: "Atmospheric Hazards #1",
  atmosphericHazard2: "Atmospheric Hazards #2",
  atmosphericHazardAction1: "Action Taken",
  atmosphericHazardActionDescription1: "Action Description",
  atmosphericHazardAction2: "Action Taken",
  atmosphericHazardActionDescription2: "Action Description",
  generalHazardActions: "General Hazard Actions",
  generalHazardActionDescriptions: "General Hazard Action Descriptions",
  forcedAirVentilationRequired: "Is forced air ventilation required?",
  ventilationType: "Type of Ventilation",
  ventilationTypeOption1: "Local",
  ventilationTypeOption2: "Exhaust",
  multipleUnitsRequired: "Are multiple units required?",
  oxygenResult1: "Oxygen Monitoring Result #1 (%)",
  oxygenResult2: "Oxygen Monitoring Result #2 (%)",
  oxygenResult3: "Oxygen Monitoring Result #3 (%)",
  hydrogenSulfideResult1: "Hydrogen Sulfide Result #1 (ppm)",
  hydrogenSulfideResult2: "Hydrogen Sulfide Result #2 (ppm)",
  hydrogenSulfideResult3: "Hydrogen Sulfide Result #3 (ppm)",
  carbonMonoxideResult1: "Carbon Monoxide Result #1 (ppm)",
  carbonMonoxideResult2: "Carbon Monoxide Result #2 (ppm)",
  carbonMonoxideResult3: "Carbon Monoxide Result #3 (ppm)",
  lowerExplosiveLimitResult1: "Lower Explosive Limit Result #1 (%)",
  lowerExplosiveLimitResult2: "Lower Explosive Limit Result #2 (%)",
  lowerExplosiveLimitResult3: "Lower Explosive Limit Result #3 (%)",
  gasDetectorModel: "Model number or type",
  calibrationDaysLeft: "Days left on calibration",
  additionalNotes: "Additional notes about the space and entry (including whether evacuation was necessary)",
    responsiblePersonName: "Name",
  responsiblePersonJobTitle: "Job Title",

        };
    }

  


} 
